import { auth } from '../firebase/config';
import { recordAuditLogAidm } from '../api/aidmAuditApi';

const SESSION_KEY_PREFIX = 'ieum.audit.session.';
const ROUTE_DEBOUNCE_MS = 1500;
const MAX_QUEUE_DEPTH = 100;

let queue = Promise.resolve();
let queued = 0;
const recentRoutes = new Map();
let accessLocationPromise = null;

const getAccessLocation = () => {
  if (accessLocationPromise) return accessLocationPromise;
  if (typeof navigator === 'undefined' || !navigator.geolocation || typeof window === 'undefined') {
    accessLocationPromise = Promise.resolve(null);
    return accessLocationPromise;
  }
  accessLocationPromise = new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 5 * 60 * 1000 },
    );
  });
  return accessLocationPromise;
};

const getProfileMeta = (profile = {}) => ({
  uid: profile?.uid || auth.currentUser?.uid || '',
  role: profile?.role || '',
  status: profile?.status || '',
});

const getLocationMeta = () => {
  if (typeof window === 'undefined') return {};
  return {
    href: window.location.href,
    path: window.location.pathname,
    search: window.location.search,
  };
};

const runAudit = (event) => {
  if (!auth.currentUser) return Promise.resolve(null);
  if (queued >= MAX_QUEUE_DEPTH) return Promise.resolve(null);
  queued += 1;
  queue = queue
    .catch(() => null)
    .then(async () => recordAuditLogAidm({ ...event, accessLocation: await getAccessLocation() }))
    .catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[audit] failed to record audit log', err);
      }
      return null;
    })
    .finally(() => {
      queued = Math.max(0, queued - 1);
    });
  return queue;
};

export const auditEvent = ({
  action,
  targetType = null,
  targetId = null,
  actorRole = null,
  before = null,
  after = null,
  meta = null,
} = {}) => {
  if (!action || !auth.currentUser) return Promise.resolve(null);
  return runAudit({
    action,
    actorRole,
    targetType,
    targetId,
    before,
    after,
    meta: {
      ...getLocationMeta(),
      ...(meta || {}),
    },
  });
};

export const auditMutation = (event) => auditEvent(event);

export const auditSessionStarted = (profile, meta = {}) => {
  const uid = profile?.uid || auth.currentUser?.uid;
  if (!uid) return Promise.resolve(null);
  try {
    const key = `${SESSION_KEY_PREFIX}${uid}`;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key) === '1') {
      return Promise.resolve(null);
    }
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, '1');
  } catch {}

  return auditEvent({
    action: 'auth.session.start',
    targetType: 'user',
    targetId: uid,
    actorRole: profile?.role || null,
    after: getProfileMeta(profile),
    meta: { source: 'auth', ...meta },
  });
};

export const auditLogout = (profile) => {
  const uid = profile?.uid || auth.currentUser?.uid;
  if (!uid) return Promise.resolve(null);
  return auditEvent({
    action: 'auth.logout',
    targetType: 'user',
    targetId: uid,
    actorRole: profile?.role || null,
    before: getProfileMeta(profile),
    meta: { source: 'auth' },
  });
};

export const auditConsentRecorded = (profile, result = {}) => {
  const uid = profile?.uid || auth.currentUser?.uid;
  if (!uid) return Promise.resolve(null);
  return auditEvent({
    action: 'consent.record',
    targetType: 'user_consent',
    targetId: uid,
    actorRole: profile?.role || null,
    after: {
      consentCompletedAt: profile?.consentCompletedAt || profile?.consent_completed_at || '',
      consentedTermKeys: profile?.consentedTermKeys || profile?.consented_term_keys || [],
    },
    meta: { source: 'consent_gate', ...result },
  });
};

export const auditRouteView = ({
  path,
  search = '',
  navigationType = '',
  role = '',
  userData = null,
} = {}) => {
  const uid = userData?.uid || auth.currentUser?.uid;
  if (!uid || !path) return Promise.resolve(null);

  const key = `${uid}:${path}:${search}`;
  const now = Date.now();
  if ((recentRoutes.get(key) || 0) + ROUTE_DEBOUNCE_MS > now) return Promise.resolve(null);
  recentRoutes.set(key, now);

  return auditEvent({
    action: 'route.view',
    targetType: 'route',
    targetId: path,
    actorRole: role || userData?.role || null,
    after: { path, search },
    meta: { navigationType },
  });
};

export const auditWebViewMessage = (
  messageType,
  payload = {},
  direction = 'inbound',
  profile = null,
) => {
  if (!auth.currentUser || !messageType) return Promise.resolve(null);
  const safePayload = {
    type: messageType,
    direction,
    platform: payload?.platform || '',
    hasToken: Boolean(payload?.token),
    keys: payload && typeof payload === 'object' ? Object.keys(payload).slice(0, 20) : [],
  };
  return auditEvent({
    action: `webview.${direction}.${messageType}`,
    targetType: 'webview_message',
    targetId: messageType,
    actorRole: profile?.role || null,
    after: safePayload,
    meta: { source: 'webview_bridge' },
  });
};
